import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { TimelineShift } from '@wilson/interfaces';
import { CheckShiftActivityPermissionsService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';
import { UnassignShiftCommonOperationsService } from '../../services/unassign-shift-common-operations.service';
import { GroupAssignmentConfirmationModalResult } from '../group-assignment-confirmation-modal/group-assignment-confirmation-modal.component';
import {
  BuildShiftsForGroupUnassignParams,
  GroupAssignmentHelperService,
  UnassignAllGroupItemsParams,
} from '../group-assignment-confirmation-modal/group-assignment-helper.service';

interface UnassignShiftDropView {
  buildShiftsForGroupUnassign: (
    params: BuildShiftsForGroupUnassignParams,
  ) => TimelineShift[];
  unassignAllGroupItems: (params: UnassignAllGroupItemsParams) => Promise<void>;
  unassignSingle: (shiftsToUnassign: TimelineShift[]) => Promise<void>;
  undo: (shiftsToUnassign: TimelineShift[]) => void;
}

@Injectable()
export class UnassignShiftDropHelperService {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly groupAssignmentHelperService = inject(
    GroupAssignmentHelperService,
  );
  private readonly unassignShiftCommonOperationsService = inject(
    UnassignShiftCommonOperationsService,
  );
  private readonly checkShiftActivityPermissionsService = inject(
    CheckShiftActivityPermissionsService,
  );
  private readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  private readonly standardUnassignDropView: UnassignShiftDropView = {
    buildShiftsForGroupUnassign: (params) =>
      this.groupAssignmentHelperService.buildShiftsForGroupUnassignInStandardView(
        params,
      ),
    unassignAllGroupItems: (params) =>
      this.groupAssignmentHelperService.unassignAllGroupItemsInStandardView(
        params,
      ),
    unassignSingle: (shiftsToUnassign) =>
      this.unassignShiftCommonOperationsService.handleStandardViewUnassign(
        shiftsToUnassign,
      ),
    undo: (shiftsToUnassign) =>
      this.unassignShiftCommonOperationsService.undoStandardViewUnassign(
        shiftsToUnassign,
      ),
  };

  private readonly cellUnassignDropView: UnassignShiftDropView = {
    buildShiftsForGroupUnassign: (params) =>
      this.groupAssignmentHelperService.buildShiftsForGroupUnassignInCellView(
        params,
      ),
    unassignAllGroupItems: (params) =>
      this.groupAssignmentHelperService.unassignAllGroupItemsInCellView(params),
    unassignSingle: (shiftsToUnassign) =>
      this.unassignShiftCommonOperationsService.handleCellViewUnassign(
        shiftsToUnassign,
      ),
    undo: (shiftsToUnassign) =>
      this.unassignShiftCommonOperationsService.undoCellViewUnassign(
        shiftsToUnassign,
      ),
  };

  public async handleStandardViewShiftDrop(
    droppedShift: TimelineShift,
    refreshUnassignedRegionIfCollapsed: () => void,
  ): Promise<void> {
    await this.handleShiftDrop(
      droppedShift,
      this.standardUnassignDropView,
      refreshUnassignedRegionIfCollapsed,
    );
    this.timelineShiftDragAndDropDirectiveHelperService.handleDragEnd(false);
  }

  public async handleCellViewShiftDrop(
    droppedShift: TimelineShift,
    refreshUnassignedRegionIfCollapsed: () => void,
  ): Promise<void> {
    await this.handleShiftDrop(
      droppedShift,
      this.cellUnassignDropView,
      refreshUnassignedRegionIfCollapsed,
    );
    this.timelineShiftDragAndDropDirectiveHelperService.handleCellViewDragEnd();
  }

  private async handleShiftDrop(
    droppedShift: TimelineShift,
    view: UnassignShiftDropView,
    refreshUnassignedRegionIfCollapsed: () => void,
  ): Promise<void> {
    try {
      const { decision, groupItems } =
        await this.groupAssignmentHelperService.checkAndConfirmGroupUnassignment(
          droppedShift.id,
        );

      if (decision) {
        const isUnassignAll =
          decision === GroupAssignmentConfirmationModalResult.MoveAllItems;

        const shiftsToUnassign = isUnassignAll
          ? view.buildShiftsForGroupUnassign({
              draggingShift: droppedShift,
              groupItems,
            })
          : [droppedShift as TimelineShift & { id: string }];

        if (shiftsToUnassign.length > 0) {
          await this.checkShiftActivityPermissionsService.checkShiftActivityPermissions(
            {
              shiftIds: shiftsToUnassign.map((shift) => shift.id),
              onSuccess: async () => {
                if (isUnassignAll) {
                  await view.unassignAllGroupItems({
                    draggingShift: droppedShift,
                    groupItems,
                    shiftsToUnassign,
                  });
                } else {
                  await view.unassignSingle(shiftsToUnassign);
                  this.clearDragAfterSingleUnassign();
                }

                const previousUserIds = [
                  ...new Set(
                    shiftsToUnassign
                      .map((shift) => shift.userId)
                      .filter((userId): userId is string => !!userId),
                  ),
                ];
                if (previousUserIds.length > 0) {
                  void this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
                    previousUserIds,
                  );
                }

                queueMicrotask(refreshUnassignedRegionIfCollapsed);
              },
              onFailure: () => {
                view.undo(shiftsToUnassign);
                this.cancelDrag();
              },
            },
          );
        } else {
          this.cancelDrag();
        }
      } else {
        this.cancelDrag();
      }
    } catch (error) {
      captureException(error);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.cancelDrag();
    }
  }

  private clearDragAfterSingleUnassign(): void {
    this.dragAndDropModeService.setDraggingShiftV2(null);
    this.dragAndDropModeService.setDraggingItemId(null);
    this.store.dispatch(new SetDemarcatorRange([]));
  }

  private cancelDrag(): void {
    this.clearDragAfterSingleUnassign();
  }
}
